import apiClient from '../utils/apiClient';
import { handleApiError } from '../utils/apiService';

const ENDPOINTS = {
  LOGIN_HISTORY: '/v2/auth/login-history',
  LOGIN_ANALYTICS: '/v2/auth/login-analytics'
};

const authService = {
  /**
   * Get login history for a specific user
   * @param {string} userId - The user ID to query
   * @param {string} startDate - ISO 8601 date string (optional)
   * @param {string} endDate - ISO 8601 date string (optional)
   * @param {number} limit - Maximum number of events to return (default 100)
   * @param {function} enqueueSnackbar - Optional snackbar function for error handling
   * @returns {Promise<Object>} Login history data
   */
  getLoginHistory: async (userId, startDate = null, endDate = null, limit = 100, enqueueSnackbar = null) => {
    try {
      // Build query params
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (limit) params.append('limit', limit.toString());
      
      const queryString = params.toString();
      const url = `${ENDPOINTS.LOGIN_HISTORY}/${userId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiClient.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching login history:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  /**
   * Get login analytics for multiple users within a time window
   * @param {string[]} userIds - Array of user IDs to query
   * @param {string} startDate - ISO 8601 date string (optional)
   * @param {string} endDate - ISO 8601 date string (optional)
   * @param {function} enqueueSnackbar - Optional snackbar function for error handling
   * @returns {Promise<Object>} Analytics data with login counts per user
   */
  getLoginAnalytics: async (userIds, startDate = null, endDate = null, enqueueSnackbar = null) => {
    try {
      const response = await apiClient.post(ENDPOINTS.LOGIN_ANALYTICS, {
        user_ids: userIds,
        start_date: startDate,
        end_date: endDate
      });
      return response;
    } catch (error) {
      console.error('Error fetching login analytics:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  /**
   * Get login history for the last N days for a user
   * @param {string} userId - The user ID to query
   * @param {number} days - Number of days to look back (default 7)
   * @param {function} enqueueSnackbar - Optional snackbar function for error handling
   * @returns {Promise<Object>} Login history data
   */
  getRecentLoginHistory: async (userId, days = 7, enqueueSnackbar = null) => {
    const endDate = new Date().toISOString();
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    
    return authService.getLoginHistory(userId, startDate, endDate, 1000, enqueueSnackbar);
  }
};

export default authService;
